<template>
  <Modal :title="title" :max-width="880" :error="error" @close="emit('close')">
    <div class="resolveBody">
      <div class="resolveCol resolveColLeft">
        <div class="colTitle">Choose approach</div>

        <div class="segmentedChoice">
          <button
            v-for="opt in OUTCOME_OPTIONS"
            :key="opt.value"
            type="button"
            class="segmentedSeg"
            :class="{ segmentedSegActive: outcome === opt.value }"
            @click="outcome = opt.value"
          >{{ opt.label }}</button>
        </div>

        <p class="outcomeDescription">{{ outcomeDescription }}</p>

        <label class="commentLabel">
          <span class="commentTitle">{{ commentLabel }} <span class="commentOptional">(optional)</span></span>
          <textarea v-model="comment" class="commentInput" rows="5" />
        </label>
      </div>

      <div class="resolveCol resolveColRight">
        <div class="attentionEntry">
          <div class="attentionEntryHeader">
            <div v-if="accountName || accountDomain" class="attentionEntryAccount">
              <img
                v-if="accountDomain"
                :src="googleFaviconUrl(accountDomain, { size: 16 })"
                class="attentionEntryFavicon"
                alt=""
                loading="lazy"
              />
              <span class="attentionEntryAccountName">{{ accountName || accountDomain }}</span>
              <span v-if="accountName && accountDomain" class="attentionEntryDomain">{{ accountDomain }}</span>
            </div>
            <span class="n-pill"><span class="n-pillDot n-pillDotDanger" />Needs attention</span>
          </div>
          <div class="attentionEntryContactRow">
            <AppIcon name="contact" :size="12" />
            <span class="attentionEntryContact">{{ contactLabel }}</span>
          </div>
          <div class="attentionEntryPlayRow">
            <span class="attentionEntryPlay">{{ playName }}</span>
            <span class="attentionEntrySep">·</span>
            <span class="attentionEntryPlay attentionEntryPlayShrink">{{ humanize(actionType) }}</span>
          </div>
          <div v-if="reason" class="attentionEntryReason">{{ reason }}</div>
          <div v-if="updatedAt" class="attentionEntryDate">{{ formatDate(updatedAt) }}</div>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="btn secondary" :disabled="busy" @click="emit('close')">Cancel</button>
      <button class="btn primary" :disabled="busy" @click="submit">
        {{ busy ? 'Working…' : 'Resolve' }}
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { googleFaviconUrl } from '../../services/favicons'

const props = defineProps<{
  playId: string
  actionId: string
  actionRunId: string
  membershipId: string | null
  reason: string | null
  contactLabel: string
  playName: string
  actionType: string
  accountName?: string | null
  accountDomain?: string | null
  updatedAt?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'resolved', outcome: 'retry' | 'not_eligible' | 'disqualify'): void
}>()

type Outcome = 'retry' | 'not_eligible' | 'disqualify'

const OUTCOME_OPTIONS: { value: Outcome; label: string }[] = [
  { value: 'retry', label: 'Retry' },
  { value: 'not_eligible', label: 'Mark not eligible' },
  { value: 'disqualify', label: 'Disqualify from play' },
]

const title = 'Resolve needs attention'
const outcome = ref<Outcome>('retry')
const comment = ref('')
const busy = ref(false)
const error = ref('')

const commentLabel = computed(() => {
  if (outcome.value === 'retry') return 'Retry instructions'
  if (outcome.value === 'not_eligible') return 'Comment'
  return 'Disqualify reason'
})

const outcomeDescription = computed(() => {
  if (outcome.value === 'retry') {
    if (props.actionType === 'dialer') return 'This will retry the dialer enrollment for this candidate.'
    if (props.actionType === 'sequence') return 'This will retry the sequence enrollment for this candidate, with your additional instructions.'
    return `This will retry the ${humanize(props.actionType)} enrollment for this candidate.`
  }
  if (outcome.value === 'not_eligible') return 'This candidate will be marked not eligible for this action and left in the play. Other in-progress actions will continue normally.'
  return 'This candidate will be disqualified and removed from the play. Any in-progress actions will be cancelled.'
})

function humanize(s: string): string {
  return s.replace(/[_-]+/g, ' ').replace(/\b(\w)/g, (m) => m.toUpperCase())
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

async function submit() {
  if (busy.value) return
  if (outcome.value === 'disqualify' && !props.membershipId) return
  busy.value = true
  error.value = ''
  try {
    if (outcome.value === 'retry') {
      await apiFetch(
        `/sales-plays/${props.playId}/actions/${props.actionId}/action-runs/retry`,
        { method: 'POST', body: { action_run_ids: [props.actionRunId], comment: comment.value.trim() || null } },
      )
    } else if (outcome.value === 'not_eligible') {
      await apiFetch(
        `/sales-plays/${props.playId}/actions/${props.actionId}/action-runs/resolve-not-eligible`,
        { method: 'POST', body: { action_run_ids: [props.actionRunId], comment: comment.value.trim() || null } },
      )
    } else {
      await apiFetch('/sales-plays/memberships/bulk-exit', {
        method: 'POST',
        body: {
          membership_ids: [props.membershipId],
          exit_reason: 'disqualified',
          exit_notes: comment.value.trim() || null,
        },
      })
    }
    emit('resolved', outcome.value)
    emit('close')
  } catch (e: any) {
    error.value = e?.message || 'Failed to resolve'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.resolveBody {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
}

.resolveCol {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

.resolveColLeft {
  flex: 1 1 55%;
}

.resolveColRight {
  flex: 1 1 45%;
}

.colTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}

.outcomeDescription {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  line-height: var(--lh-normal);
}

.attentionEntry {
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
}

.attentionEntryHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.attentionEntryAccount {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.attentionEntryFavicon {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

.attentionEntryAccountName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attentionEntryDomain {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-normal);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attentionEntryContactRow {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--color-text-secondary);
}

.attentionEntryPlayRow {
  margin-top: 2px;
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.attentionEntryContact,
.attentionEntryPlay {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}

.attentionEntryPlay {
  min-width: 0;
  overflow-wrap: break-word;
}

.attentionEntryPlayShrink {
  flex-shrink: 0;
  overflow-wrap: normal;
}

.attentionEntrySep {
  flex-shrink: 0;
  color: var(--color-text-tertiary);
}

.attentionEntryReason {
  margin-top: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger, #b91c1c);
  line-height: var(--lh-normal);
}

.attentionEntryDate {
  margin-top: 2px;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}

.n-pillDotDanger { background: var(--color-danger, #b91c1c); }

.segmentedChoice {
  display: inline-flex;
  align-items: stretch;
  align-self: flex-start;
  height: 30px;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.segmentedSeg {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-3);
  background: none;
  border: none;
  border-left: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1;
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
}
.segmentedSeg:first-child { border-left: none; }
.segmentedSeg:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
.segmentedSegActive {
  color: var(--color-text-inverse);
  background: var(--color-accent);
}
.segmentedSegActive:hover { color: var(--color-text-inverse); background: var(--color-accent-hover); }

.commentLabel {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.commentTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}

.commentOptional {
  font-weight: var(--fw-normal);
  color: var(--color-text-tertiary);
}

.commentInput {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  padding: var(--space-2);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  resize: vertical;
}
</style>
